import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
// 临时修复：直接导入button组件
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // 调用自定义错误处理函数
    this.props.onError?.(error, errorInfo);
    
    // 这里可以集成错误监控服务，如 Sentry、LogRocket 等
    // Sentry.captureException(error, { extra: errorInfo });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // 如果有自定义 fallback，使用自定义 fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认错误页面
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <div className="relative inline-block mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/20 dark:to-orange-900/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-red-500 dark:text-red-400" />
              </div>
              <div className="absolute inset-0 animate-ping bg-gradient-to-br from-red-500 to-orange-500 rounded-full opacity-20" />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
              出错了！
            </h2>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              抱歉，页面遇到了一些问题。
            </p>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">错误信息:</p>
              <p className="text-sm font-mono text-red-500 dark:text-red-400 break-words">
                {this.state.error?.message || '未知错误'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={this.handleRetry}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                重试页面
              </Button>
              
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="flex-1"
              >
                <Home className="w-4 h-4 mr-2" />
                返回首页
              </Button>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                如果问题持续存在，请联系技术支持
              </p>
              <button
                onClick={() => window.location.reload()}
                className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 mt-2"
              >
                强制刷新页面
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

import type { ApiError } from '@/utils/error-handler.tsx';

export function ErrorCard({ 
  title = "出错了", 
  message, 
  error,
  onRetry,
  onDismiss 
}: {
  title?: string;
  message?: string;
  error?: Error | ApiError | string;
  onRetry?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-medium text-red-800 dark:text-red-300">
            {title}
          </h4>
          
          {message && (
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">
              {message}
            </p>
          )}

          {error && (
            <div className="mt-2">
              <p className="text-xs text-red-600 dark:text-red-500 font-mono bg-red-100 dark:bg-red-900/30 p-2 rounded break-words">
                {typeof error === 'string' ? error : error.message}
              </p>
            </div>
          )}

          {(onRetry || onDismiss) && (
            <div className="flex gap-2 mt-3">
              {onRetry && (
                <Button
                  onClick={onRetry}
                  size="sm"
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  重试
                </Button>
              )}
              {onDismiss && (
                <Button
                  onClick={onDismiss}
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  忽略
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function NetworkError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="text-center py-12">
      <div className="inline-block p-3 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/20 dark:to-orange-900/20 rounded-full mb-4">
        <AlertTriangle className="w-8 h-8 text-red-500 dark:text-red-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
        网络连接失败
      </h3>
      <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-sm mx-auto">
        无法连接到服务器，请检查网络连接或稍后重试。
      </p>
      <Button
        onClick={onRetry}
        className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
      >
        <RefreshCw className="w-4 h-4 mr-2" />
        重新连接
      </Button>
    </div>
  );
}